/**
 * @file        src/pages/account/CorporateRequests.jsx
 * @description Corporate Registration Requests — the approval queue the
 *              storefront's "Register your company" form files into instead
 *              of creating the account directly (corporate_registration_requests,
 *              migration 049). Approve runs the existing corporate-account
 *              creation flow (business_accounts + b2b_owner) with the details
 *              and password hash the applicant already submitted; Reject
 *              records an optional reason. Both decisions email the applicant
 *              (admin_backend/src/services/corporateRequests.service.js) —
 *              the response's `emailSent` flag is surfaced in the toast so the
 *              admin knows whether that delivery actually happened.
 * @author      Kinetika Labs
 * @created     2026-08-10
 * @changelog
 *   - 2026-08-10 Claude — Initial (corporate registration approval flow).
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ReadOnlyListPage, { fmtDate } from '../flight/ReadOnlyListPage';
import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import { toast } from '../../lib/toast';
import { confirm, promptText } from '../../lib/confirm';
import { approveCorporateRequest, rejectCorporateRequest } from '../../api/corporateRequests';

const STATUS_TONE = { pending: 'pend', approved: 'ok', rejected: 'rej' };
const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

export default function CorporateRequests() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [busyId, setBusyId] = useState(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['corporate-requests'] });

  const doApprove = async (row) => {
    const ok = await confirm({
      title: 'Approve registration',
      message: `Approve ${row.company_name}'s corporate account request? This creates the account and signs the applicant up with the password they chose.`,
      confirmLabel: 'Approve',
      danger: false,
    });
    if (!ok) return;
    setBusyId(row.id);
    try {
      const res = await approveCorporateRequest(row.id);
      toast.ok('Request approved', res?.data?.emailSent
        ? `${row.company_name} can now sign in — notified by email.`
        : `${row.company_name} can now sign in — the notification email did not send.`);
      invalidate();
    } catch (e) {
      toast.err('Could not approve', e?.response?.data?.error?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async (row) => {
    const reason = await promptText({
      title: 'Reject registration',
      message: `Optional reason for rejecting ${row.company_name} — this is quoted in the applicant's email.`,
      placeholder: 'e.g. PAN details could not be verified',
      confirmLabel: 'Reject',
    });
    if (reason === null) return;
    setBusyId(row.id);
    try {
      const res = await rejectCorporateRequest(row.id, reason);
      toast.ok('Request rejected', res?.data?.emailSent
        ? `${row.company_name} was notified by email.`
        : `${row.company_name} — the notification email did not send.`);
      invalidate();
    } catch (e) {
      toast.err('Could not reject', e?.response?.data?.error?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const toolbar = (
    <div className="filter-row">
      <div className="field" style={{ minWidth: 180 }}>
        <label>Status</label>
        <Select
          value={status}
          onChange={setStatus}
          placeholder="All statuses"
          options={[
            { value: '', label: 'All statuses' },
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
          ]}
        />
      </div>
    </div>
  );

  const rowActions = (row) => (
    row.status === 'pending' ? (
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn xs primary" disabled={busyId === row.id} onClick={() => doApprove(row)}>
          <Icon name="check" /> Approve
        </button>
        <button className="btn xs danger" disabled={busyId === row.id} onClick={() => doReject(row)}>
          <Icon name="x" /> Reject
        </button>
      </div>
    ) : <span className="muted">{row.review_note || '—'}</span>
  );

  return (
    <ReadOnlyListPage
      title="Corporate Registration Requests"
      subtitle="Account Management · Storefront company sign-ups awaiting approval"
      endpoint="/corporate-requests"
      queryRoot="corporate-requests"
      searchLabel="Company / Email / PAN"
      actionsLabel="Decision"
      toolbar={toolbar}
      rowActions={rowActions}
      extraParams={status ? { status } : undefined}
      columns={[
        { key: 'company_name', label: 'Company', render: (v) => <b>{v}</b> },
        {
          key: 'email',
          label: 'Contact',
          render: (v, r) => (
            <div>
              <div>{v}</div>
              <div className="muted" style={{ fontSize: '.8em' }}>{r.contact_phone || '—'}</div>
            </div>
          ),
        },
        {
          key: 'pan_number',
          label: 'PAN / GST',
          render: (v, r) => (
            <div>
              <div className="mono">{v}</div>
              <div className="muted" style={{ fontSize: '.8em' }}>{r.gst_number || '—'}</div>
            </div>
          ),
        },
        { key: 'brand', label: 'Brand' },
        { key: 'created_at', label: 'Submitted', render: fmtDate },
        { key: 'status', label: 'Status', render: (v) => <Chip tone={STATUS_TONE[v] || 'pend'}>{STATUS_LABEL[v] || v}</Chip> },
      ]}
    />
  );
}
